import { Badge, Button } from '@aibi/ui';
import { Check, Lightbulb, X } from 'lucide-react';
import type {
  AssistantCandidate,
  AssistantFact,
  AssistantReply,
} from '../../services/assistant-port';

export type ProposalStatus = 'pending' | 'applied' | 'dismissed';

const SOURCE_LABELS: Readonly<Record<AssistantFact['source'], string>> = {
  'analysis-state': 'Analisis',
  'semantic-model': 'Modelo',
  'query-plan': 'Plan',
  'query-result': 'Resultado',
};

export interface AiReplyViewProps {
  readonly reply: AssistantReply;
  readonly status: ProposalStatus;
  readonly onApply: () => void;
  readonly onDismiss: () => void;
  readonly onCandidate: (candidate: AssistantCandidate) => void;
  /** `false` si la sugerencia no puede aplicarse (sin tarjeta seleccionada). */
  readonly canApplySuggestion: boolean;
}

/**
 * Presenta una respuesta tipada del asistente. Nunca aplica cambios por si misma: cada
 * cambio requiere un clic explicito en "Aplicar" (y los cambios de modelo, ademas, una
 * confirmacion fuera del asistente).
 */
export function AiReplyView({
  reply,
  status,
  onApply,
  onDismiss,
  onCandidate,
  canApplySuggestion,
}: AiReplyViewProps) {
  switch (reply.kind) {
    case 'proposal':
      return (
        <div className="space-y-2" aria-label="Propuesta del asistente">
          <p className="font-medium">Propuesta</p>
          <ul className="list-disc space-y-0.5 pl-4">
            {reply.summary.map((line, i) => (
              <li key={`${String(i)}-${line}`}>{line}</li>
            ))}
          </ul>
          {reply.modelChanges.length > 0 && (
            <div className="space-y-1 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs">
              <Badge variant="warning">Requiere confirmacion</Badge>
              <p>
                Los cambios al modelo semantico no se aplican desde el asistente; un responsable del
                modelo debe revisarlos.
              </p>
              <ul className="list-disc pl-4">
                {reply.modelChanges.map((change) => (
                  <li key={change.actionId}>{change.summary}</li>
                ))}
              </ul>
            </div>
          )}
          {reply.state !== null && status === 'pending' && (
            <div className="flex gap-2">
              <Button size="sm" onClick={onApply}>
                <Check /> Aplicar
              </Button>
              <Button size="sm" variant="ghost" onClick={onDismiss}>
                <X /> Descartar
              </Button>
            </div>
          )}
          {status === 'applied' && <Badge variant="success">Aplicada</Badge>}
          {status === 'dismissed' && <Badge variant="outline">Descartada</Badge>}
        </div>
      );
    case 'clarification':
      return (
        <div className="space-y-2">
          <p>{reply.question}</p>
          <p className="text-xs text-muted-foreground">{reply.reason}</p>
          <CandidateList candidates={reply.candidates} onCandidate={onCandidate} />
        </div>
      );
    case 'explanation':
      return (
        <div className="space-y-2">
          <p className="font-medium">{reply.title}</p>
          <p>{reply.summary}</p>
          {reply.facts.length > 0 && (
            <dl className="space-y-1 text-xs">
              {reply.facts.map((fact, i) => (
                <div
                  key={`${String(i)}-${fact.label}`}
                  className="flex flex-wrap items-baseline gap-1"
                >
                  <dt className="font-medium">{fact.label}:</dt>
                  <dd className="flex-1">{fact.value}</dd>
                  <Badge variant="outline">{SOURCE_LABELS[fact.source]}</Badge>
                </div>
              ))}
            </dl>
          )}
        </div>
      );
    case 'suggestion':
      return (
        <div className="space-y-2">
          <p className="flex items-center gap-1 font-medium">
            <Lightbulb aria-hidden className="size-4" /> Sugerido: {reply.label}
          </p>
          <p className="text-xs text-muted-foreground">{reply.rationale}</p>
          {canApplySuggestion && status === 'pending' && (
            <Button size="sm" variant="outline" onClick={onApply}>
              <Check /> Aplicar {reply.label}
            </Button>
          )}
          {status === 'applied' && <Badge variant="success">Aplicada</Badge>}
        </div>
      );
    case 'error':
      return (
        <div className="space-y-2">
          <p>{reply.message}</p>
          {reply.candidates.length > 0 && (
            <>
              <p className="text-xs text-muted-foreground">Opciones disponibles:</p>
              <CandidateList candidates={reply.candidates} onCandidate={onCandidate} />
            </>
          )}
        </div>
      );
  }
}

function CandidateList({
  candidates,
  onCandidate,
}: {
  readonly candidates: readonly AssistantCandidate[];
  readonly onCandidate: (candidate: AssistantCandidate) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Opciones">
      {candidates.map((candidate) => (
        <Button
          key={`${candidate.kind}-${candidate.id}-${candidate.dimensionId ?? ''}`}
          size="sm"
          variant="outline"
          onClick={() => {
            onCandidate(candidate);
          }}
        >
          {candidate.label}
        </Button>
      ))}
    </div>
  );
}
